import { useCallback, useEffect, useState } from "react";
import { Siren, ShieldX, Copy, Phone, CreditCard, Ban, KeyRound, UserX, StickyNote, History, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Section({ icon: Icon, title, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-card border border-slate-100">
      <button onClick={() => setOpen(!open)} data-testid={`fraud-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className="w-full flex items-center justify-between p-5 text-left">
        <span className="flex items-center gap-3 font-semibold text-slate-800"><Icon size={17} className="text-slate-400" /> {title}</span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${count > 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-400"}`}>{count}</span>
      </button>
      {open && <div className="px-5 pb-5 border-t border-slate-100 pt-4">{children}</div>}
    </div>
  );
}

export function FraudPanel() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/fraud/overview").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const suspend = async (userId) => {
    const reason = window.prompt("Reason for suspension (sent to the audit log):") || "";
    setBusy(userId);
    try {
      const r = await api.post(`/admin/fraud/users/${userId}/suspend`, { reason });
      toast.success(r.data.message);
      load();
      if (history?.user?.id === userId) openHistory(userId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const unsuspend = async (userId) => {
    setBusy(userId);
    try {
      const r = await api.post(`/admin/fraud/users/${userId}/unsuspend`);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const reverify = async (userId) => {
    setBusy(userId);
    try {
      await api.post(`/admin/identity/users/${userId}/request-reverification`);
      toast.success("Re-verification requested — user notified by email.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const addNote = async (userId) => {
    const note = window.prompt("Internal note for this account:");
    if (!note) return;
    try {
      await api.post(`/admin/fraud/users/${userId}/note`, { note });
      toast.success("Note saved.");
      load();
      if (history?.user?.id === userId) openHistory(userId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openHistory = async (userId) => {
    try {
      const r = await api.get(`/admin/fraud/users/${userId}/history`);
      setHistory(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <p className="text-slate-400 text-sm">Loading…</p>;
  const s = data.stats;

  const UserActions = ({ userId, suspended }) => userId ? (
    <span className="flex items-center gap-1.5 shrink-0">
      <button onClick={() => openHistory(userId)} title="Full history" data-testid={`fraud-history-${userId}`} className="text-slate-400 hover:text-blue-600 p-1"><History size={15} /></button>
      <button onClick={() => addNote(userId)} title="Add note" data-testid={`fraud-note-${userId}`} className="text-slate-400 hover:text-amber-600 p-1"><StickyNote size={15} /></button>
      <button onClick={() => reverify(userId)} disabled={busy === userId} title="Request re-verification" data-testid={`fraud-reverify-${userId}`} className="text-slate-400 hover:text-purple-600 p-1 disabled:opacity-40"><KeyRound size={15} /></button>
      {suspended ? (
        <button onClick={() => unsuspend(userId)} disabled={busy === userId} title="Reinstate" className="text-green-500 hover:text-green-700 p-1 disabled:opacity-40"><UserX size={15} /></button>
      ) : (
        <button onClick={() => suspend(userId)} disabled={busy === userId} title="Suspend account" data-testid={`fraud-suspend-${userId}`} className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-40"><Ban size={15} /></button>
      )}
    </span>
  ) : null;

  const cards = [
    ["Failed verifications", s.failed_verifications], ["Duplicate identities", s.duplicate_identities],
    ["Shared phones", s.shared_phones], ["Failed payments", s.failed_payments],
    ["Repeat cancellers", s.repeat_cancellers], ["Failed logins", s.failed_logins],
    ["Suspended", s.suspended_accounts],
  ];

  return (
    <div data-testid="fraud-panel" className="space-y-6">
      <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-5 flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-red-50 text-red-500"><Siren size={19} /></span>
        <div>
          <p className="font-heading font-bold text-slate-900">Fraud Center</p>
          <p className="text-slate-400 text-xs">Identity failures, duplicate accounts, payment risk and suspicious activity — with suspend, notes and full history per account.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map(([label, value]) => (
          <div key={label} className="bg-white rounded-xl shadow-card border border-slate-100 p-3.5 text-center">
            <p className={`font-heading text-xl font-bold ${value > 0 ? "text-red-600" : "text-slate-900"}`}>{value}</p>
            <p className="text-slate-400 text-[10px] mt-0.5 leading-tight">{label}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <Section icon={ShieldX} title="Failed identity verifications" count={data.failed_verifications.length}>
          {data.failed_verifications.length === 0 ? <p className="text-slate-400 text-sm">None 🎉</p> : data.failed_verifications.map((v) => (
            <div key={v.id} className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-50 text-sm">
              <div>
                <p className="font-semibold text-slate-800">{v.subject_name || "—"} <span className="text-slate-400 font-normal">{v.subject_email}</span></p>
                <p className="text-xs text-slate-400">{v.subject_type === "driver" ? "Additional driver" : "Renter"} · <span className="text-red-500 font-semibold">{v.status}</span> · {(v.updated_at || "").slice(0, 10)}</p>
              </div>
              <UserActions userId={v.user_id} suspended={v.suspended} />
            </div>
          ))}
        </Section>

        <Section icon={Copy} title="Duplicate identities (same license)" count={data.duplicate_identities.length}>
          {data.duplicate_identities.length === 0 ? <p className="text-slate-400 text-sm">None detected.</p> : data.duplicate_identities.map((d) => (
            <div key={d.document_number} className="py-2.5 border-b border-slate-50 text-sm">
              <p className="font-mono text-xs text-red-600 font-bold">License {d.document_number} — {d.count} accounts</p>
              {d.users.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-3 mt-1.5 pl-3">
                  <span className="text-slate-700">{u.name} <span className="text-slate-400">{u.email}</span></span>
                  <UserActions userId={u.id} />
                </div>
              ))}
            </div>
          ))}
        </Section>

        <Section icon={Phone} title="Accounts sharing a phone number" count={data.shared_phones.length}>
          {data.shared_phones.length === 0 ? <p className="text-slate-400 text-sm">None detected.</p> : data.shared_phones.map((d) => (
            <div key={d.phone} className="py-2.5 border-b border-slate-50 text-sm">
              <p className="font-mono text-xs text-amber-600 font-bold">{d.phone} — {d.count} accounts</p>
              {d.users.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-3 mt-1.5 pl-3">
                  <span className="text-slate-700">{u.name} <span className="text-slate-400">{u.email}</span></span>
                  <UserActions userId={u.id} />
                </div>
              ))}
            </div>
          ))}
        </Section>

        <Section icon={CreditCard} title="Failed / expired payments" count={data.failed_payments.length}>
          {data.failed_payments.length === 0 ? <p className="text-slate-400 text-sm">None.</p> : data.failed_payments.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 text-sm">
              <span className="text-slate-700">Booking <span className="font-mono text-xs">{(p.booking_id || "").slice(0, 8)}</span></span>
              <span className="text-xs text-slate-400">{p.payment_status} · {(p.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </Section>

        <Section icon={X} title="Repeat cancellers (2+ cancelled trips)" count={data.repeat_cancellers.length}>
          {data.repeat_cancellers.length === 0 ? <p className="text-slate-400 text-sm">None.</p> : data.repeat_cancellers.map((c) => (
            <div key={c.email} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 text-sm">
              <span className="text-slate-700">{c.name} <span className="text-slate-400">{c.email}</span></span>
              <span className="text-xs font-bold text-red-600">{c.count} cancellations</span>
            </div>
          ))}
        </Section>

        <Section icon={KeyRound} title="Suspicious login activity" count={data.failed_logins.length}>
          {data.failed_logins.length === 0 ? <p className="text-slate-400 text-sm">None recorded.</p> : data.failed_logins.slice(0, 50).map((e, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-50 text-xs">
              <span className="text-slate-700">{e.email} <span className="text-slate-400">· {e.type === "suspended_login_attempt" ? "suspended account attempt" : "wrong password"}</span></span>
              <span className="text-slate-400 font-mono">{e.ip} · {(e.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </Section>

        <Section icon={Ban} title="Suspended accounts" count={data.suspended.length}>
          {data.suspended.length === 0 ? <p className="text-slate-400 text-sm">None.</p> : data.suspended.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-50 text-sm">
              <div>
                <p className="font-semibold text-slate-800">{u.name} <span className="text-slate-400 font-normal">{u.email}</span></p>
                <p className="text-xs text-slate-400">{(u.suspended_at || "").slice(0, 10)}{u.suspended_reason ? ` · ${u.suspended_reason}` : ""}</p>
              </div>
              <button onClick={() => unsuspend(u.id)} disabled={busy === u.id} data-testid={`fraud-unsuspend-${u.id}`}
                className="text-xs font-semibold text-green-700 border border-green-200 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 disabled:opacity-50">Reinstate</button>
            </div>
          ))}
        </Section>

        <Section icon={StickyNote} title="Internal notes" count={data.notes.length}>
          {data.notes.length === 0 ? <p className="text-slate-400 text-sm">No notes yet — add one from any account row.</p> : data.notes.map((n) => (
            <div key={n.id} className="py-2.5 border-b border-slate-50 text-sm">
              <p className="text-slate-700">{n.note}</p>
              <p className="text-xs text-slate-400 mt-0.5">{n.user_name} ({n.user_email}) · by {n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
          ))}
        </Section>
      </div>

      {history && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-5" data-testid="fraud-history-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-7">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                  {history.user.name}
                  {history.user.suspended && <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">SUSPENDED</span>}
                  {history.user.identity_verified && <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">VERIFIED</span>}
                </h3>
                <p className="text-slate-400 text-xs mt-0.5">{history.user.email}{history.user.phone ? ` · ${history.user.phone}` : ""} · joined {(history.user.created_at || "").slice(0, 10)}</p>
              </div>
              <button onClick={() => setHistory(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>

            {[["Identity checks", history.verifications.map((v) => `${v.status} · ${(v.created_at || "").slice(0, 10)}${v.status_code ? ` · code ${v.status_code}` : ""}`)],
              ["Bookings", history.bookings.map((b) => `${b.car_name} · ${b.pickup_date} → ${b.return_date} · ${b.status} / ${b.payment_status} · $${b.amount_due}`)],
              ["Security events", history.security_events.map((e) => `${e.type} · ${e.ip} · ${(e.at || "").slice(0, 16).replace("T", " ")}`)],
              ["Notes", history.notes.map((n) => `${n.note} — ${n.by}, ${(n.at || "").slice(0, 10)}`)]].map(([title, rows]) => (
              <div key={title} className="mt-5">
                <p className="label-caps text-slate-500 text-[11px]">{title} ({rows.length})</p>
                {rows.length === 0 ? <p className="text-slate-400 text-xs mt-1.5">None.</p> : (
                  <ul className="mt-1.5 space-y-1">
                    {rows.slice(0, 15).map((r, i) => <li key={i} className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5">{r}</li>)}
                  </ul>
                )}
              </div>
            ))}

            <div className="flex gap-3 mt-6">
              <button onClick={() => addNote(history.user.id)} className="flex-1 border-2 border-slate-200 text-slate-600 py-2 rounded-full text-sm font-semibold hover:bg-slate-50">Add note</button>
              {history.user.suspended ? (
                <button onClick={() => { unsuspend(history.user.id); setHistory(null); }} className="flex-1 bg-green-600 text-white py-2 rounded-full text-sm font-semibold hover:bg-green-700">Reinstate</button>
              ) : (
                <button onClick={() => { suspend(history.user.id); }} className="flex-1 bg-red-600 text-white py-2 rounded-full text-sm font-semibold hover:bg-red-700">Suspend</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
